import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { isLoggedIn } from 'utils/helpers'
import uploadFileToS3 from 'apis/aws'
// import NftImageEditorModal from 'components/Modal/NftImageEditorModal'
import Alert from 'components/Alert'

// next components
import { useWalletModal } from '@solana/wallet-adapter-react-ui'

// custom components
import BrowseArtistCard from 'components/Card/BrowseArtistCard'
import ReadMoreCollectionModal from 'components/Modal/ReadMoreCollectionModal'

// custom functions
// import { namePublicFile } from 'utils'

// images and icons
import avatarImage from 'assets/images/collection-avatar.png'
import PlusIcon from 'assets/icons/plus-icon-8x8.svg?component'
import MinusIcon from 'assets/icons/minus-icon-8x8.svg?component'

// styles
import styles from './index.module.scss'

// api
import collectionAPI from 'apis/collections'
import SocialShareModal from 'components/Menu/SocialShareModal'
import CollectionDetailModal from 'components/Modal/CollectionDetailModal'
import AddTokenModal from 'components/Modal/AddTokenModal'
import ImportEAModal from 'components/Modal/ImportEAModal'
import { useRouter } from 'next/router'
import OutlineButton from 'components/Button/OutlineButton'

const MainSection = props => {
  const {
    nfts,
    data,
    publicKey,
    hasloading,
    setHasLoading,
    collectionId,
    coverUrl,
    collectionName,
    handleCollectionImport,
    setActiveTab,
    activeTab,
  } = props

  const dispatch = useDispatch()
  const router = useRouter()

  const [items, setItems] = useState<any>()
  const [owners, setOwners] = useState<any>()
  const [floorPrice, setFloorPrice] = useState<any>()
  const [totalVolume, setTotalVolume] = useState<number>(0.0)
  const [saved, setSaved] = useState<boolean>(false)
  const [collectionData, setCollectionData] = useState<any>()
  const [showAddTokenModal, setShowAddTokenModal] = useState(false)
  const [showImportEAModal, setShowImportEAModal] = useState(false)
  const [openShareModal, setOpenShareModal] = useState(false)
  const { setVisible } = useWalletModal()
  const [showAlert, setShowAlert] = useState(false)
  const [alertData, setAlertData] = useState<any[]>([])
  const [editCollection, setEditCollection] = useState(false)

  const [showStatistics, setShowStatistics] = useState(false)

  useEffect(() => {
    setCollectionData(data)
  }, [data])

  useEffect(() => {
    if (data) {
      setTotalVolume(data.total_volume)
      setSaved(data['saved'])
    } else {
      setTotalVolume(0)
    }
  }, [data])

  useEffect(() => {
    setItems(nfts?.length || 0)

    if (nfts) {
      setOwners(new Set(nfts.map(nft => nft.ownership.owner)).size)
    }

    let prices = []
    if (nfts)
      nfts.forEach(nft => {
        if (nft?.list_price) prices.push(parseFloat(nft.list_price))
      })
    setFloorPrice(prices.length === 0 ? 0 : Math.min.apply(Math, prices))
  }, [nfts])

  const saveCollection = collectionId => {
    if (isLoggedIn()) {
      collectionAPI.saveCollection(collectionId).then(
        response => {
          if (response) setSaved(true)
        },
        error => {}
      )
    } else {
      dispatch({ type: 'set', loginAndRefresh: true })
      setVisible(true)
    }
  }

  const unsaveCollection = collectionId => {
    collectionAPI.unsaveCollection(collectionId).then(
      response => {
        setSaved(false)
      },
      error => {}
    )
  }

  const uploadNew = () => {
    dispatch({ type: 'set', mintCollectionStep: 3 })
    dispatch({ type: 'set', collectionMintAddress: collectionId })
    dispatch({
      type: 'set',
      collectionMintCategories: collectionData?.categories.length > 0 ? collectionData?.categories.split(',') : [],
    })
    dispatch({ type: 'set', collectionCoverUrl: coverUrl })
    dispatch({ type: 'set', collectionMintName: collectionName })
    dispatch({ type: 'set', collectionKind: 'foster' })
    router.push('/create/collection')
  }

  return (
    <div>
      <div className="mt-[30px] lg:mt-[60px] w-full">
        <BrowseArtistCard
          isOwn={collectionData?.minter_id === publicKey?.toBase58()}
          image={collectionData?.cover_image ?? avatarImage.src}
          collectionName={collectionData?.collection_name}
          artistName={collectionData?.artist_username}
          artistImage={collectionData?.artist_image}
          description={collectionData?.description}
          artistAddress={collectionData?.artist_username}
          hasloading={hasloading}
          onClickEdit={() => setEditCollection(true)}
          setOpenShareModal={setOpenShareModal}
          saved={saved}
          saveCollection={() => {
            saved ? unsaveCollection(collectionData?.collection_id) : saveCollection(collectionData?.collection_id)
          }}
          addTokenIds={() => setShowAddTokenModal(true)}
          importArtwork={() => setShowImportEAModal(true)}
          uploadNew={() => uploadNew()}
        />

        <div>
          {hasloading ? (
            <div className="w-full h-[40px] rounded-[20px] overflow-hidden mt-[30px]">
              <div className="skeletonLoader" />
            </div>
          ) : (
            <div
              className="w-full flex justify-between items-center mt-[30px]"
              onClick={() => setShowStatistics(!showStatistics)}
            >
              <div className={styles.statistics}>Statistics</div>
              <div>{showStatistics ? <MinusIcon /> : <PlusIcon />}</div>
            </div>
          )}
          {!hasloading && <div className={`mt-[15px] ${styles.divider}`} />}

          {showStatistics && (
            <div className="mt-[15px]">
              <div className="w-full flex justify-between items-center">
                <div className={styles.specLabel}>Artwork</div>
                <div className={styles.specValue}>{items}</div>
              </div>
              <div className="w-full flex justify-between items-center mt-[6px]">
                <div className={styles.specLabel}>Collectors</div>
                <div className={styles.specValue}>{owners}</div>
              </div>
              <div className="w-full flex justify-between items-center mt-[6px]">
                <div className={styles.specLabel}>Floor Price</div>
                <div className={styles.specValue}>{floorPrice > 0 ? floorPrice : '0'}&nbsp;SOL</div>
              </div>
              <div className="w-full flex justify-between items-center mt-[6px]">
                <div className={styles.specLabel}>Total Volume</div>
                <div className={styles.specValue}>{totalVolume > 0 ? totalVolume.toFixed(2) : '0'}&nbsp;SOL</div>
              </div>
            </div>
          )}
        </div>

        {hasloading ? (
          <div className="w-full flex flex-col gap-[7px] mt-[30px]">
            <div className="w-full h-[45px] rounded-[23px] overflow-hidden">
              <div className="skeletonLoader" />
            </div>
            <div className="w-full h-[45px] rounded-[23px] overflow-hidden">
              <div className="skeletonLoader" />
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col gap-[7px] mt-[30px]">
            <OutlineButton
              label="Artwork"
              borderColor={
                activeTab === 0 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
              }
              backgroundColor="var(--bg-151515-E8E7E5)"
              color={activeTab === 0 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
              fontFamily="Jost-Regular"
              borderRadius={5}
              onClick={() => {
                setActiveTab(0)
              }}
            />

            <OutlineButton
              label="Activity"
              borderColor={
                activeTab === 1 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
              }
              backgroundColor="var(--bg-151515-E8E7E5)"
              borderRadius={5}
              fullWidth
              color={activeTab === 1 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
              fontFamily="Jost-Regular"
              onClick={() => {
                setActiveTab(1)
              }}
            />
          </div>
        )}
      </div>

      {/* <NftImageEditorModal
        open={openBannerEditorModal}
        close={() => setOpenBannerEditorModal(false)}
        nftImage={banner}
        getCroppedImage={croppedImage => {
          setUpdateBanner(true)
          setCroppedBanner(croppedImage)
          setOpenBannerEditorModal(false)
        }}
        dimension={{ value: [1080, 320] }}
      /> */}
      <Alert
        open={showAlert}
        close={() => {
          setShowAlert(false)
        }}
        data={alertData}
      />
      {openShareModal && (
        <SocialShareModal
          open={openShareModal}
          close={() => setOpenShareModal(false)}
          vote={true}
          message={'Check out this collection on @foster_protocol #fostermarketplace'}
          url={`/collection/${collectionData?.collection_id}`}
        />
      )}

      {editCollection && (
        <CollectionDetailModal
          open={editCollection}
          close={() => setEditCollection(false)}
          data={collectionData}
          onSuccess={response => {
            setEditCollection(false)
            let newData = collectionData
            newData.collection_name = response.collection_name
            newData.description = response.description
            newData.cover_image = response.cover_image
            setCollectionData(newData)

            setHasLoading(true)
            setTimeout(() => {
              setHasLoading(false)
            }, 1000)
          }}
        />
      )}

      <AddTokenModal
        open={showAddTokenModal}
        onClose={() => setShowAddTokenModal(false)}
        onImportMintList={mintList => {
          setShowAddTokenModal(false)
          handleCollectionImport({ mintList })
        }}
      />

      <ImportEAModal
        open={showImportEAModal}
        onClose={() => setShowImportEAModal(false)}
        onImportUrl={eaUrl => handleCollectionImport({ eaUrl })}
      />
    </div>
  )
}

export default MainSection
